import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetStudentAssignmentDetailFromTeacher } from "@packages/api/madlenAPI";
import { StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem } from "@packages/api/madlenAPI.schemas";
import { ArrowLeft } from "lucide-react";
import { QuestionViewer } from "../../SingleAssignment/componnets/questions/QuestionViewer";
import { Text, Button } from "@core/index";
import { useMemo } from "react";

const SingleStudentAssignmentView = () => {
  const { studentId, assignmentId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: assignmentData, isLoading: isAssignmentLoading } =
    useGetStudentAssignmentDetailFromTeacher(
      Number(studentId),
      Number(assignmentId),
    );

  const order = useMemo(() => ["OE", "TEST", "TF", "FB"], []);

  const questionsWithStudentAnswers = useMemo(() => {
    if (!assignmentData || !assignmentData.questionsWithStudentAnswers)
      return [];
    return assignmentData.questionsWithStudentAnswers.sort((a, b) => {
      const typeOrder =
        order.indexOf(a.question.type) - order.indexOf(b.question.type);
      return typeOrder === 0 ? a.question.id - b.question.id : typeOrder;
    });
  }, [assignmentData, order]);

  if (isAssignmentLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-32 bg-gray-200 dark:bg-gray-700 rounded"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!assignmentData || !assignmentData.questionsWithStudentAnswers) {
    return <div className="p-6">Failed to load assignment data</div>;
  }

  const totalPoints = assignmentData.questionsWithStudentAnswers.reduce(
    (
      sum: number,
      q: StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem,
    ) => sum + q.question.point,
    0,
  );

  const earnedPoints = assignmentData.questionsWithStudentAnswers.reduce(
    (
      sum: number,
      answer: StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem,
    ) => sum + (answer.score ?? 0),
    0,
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header with summary */}
      {/* Back Button */}
      <Button
        onClick={() => navigate(-1)}
        variant="solid"
        size="icon"
        rounded="lg"
        textColor="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        className="mb-4"
      >
        <ArrowLeft className="w-5 h-5" />
      </Button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <Text variant="h1" className="mb-4">
          {assignmentData.assignmentName}
        </Text>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
            <div className="text-green-800 dark:text-green-100 text-sm font-medium">
              {t("assignments.student.points")}
            </div>
            <div className="text-2xl font-bold text-green-900 dark:text-green-100">
              {earnedPoints} / {totalPoints}
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg">
            <div className="text-purple-800 dark:text-purple-100 text-sm font-medium">
              {t("assignments.student.correctAnswers")}
            </div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {
                assignmentData.questionsWithStudentAnswers.filter(
                  (a) => a.question.point === a.score,
                ).length
              }{" "}
              / {assignmentData.questionsWithStudentAnswers.length}
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
            <div className="text-blue-800 dark:text-blue-100 text-sm font-medium">
              {t("assignments.generator.questions.duration")}
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {(() => {
                const startDate = new Date(assignmentData.studentStartDate);
                const endDate = new Date(assignmentData.studentEndDate);
                const diffMs = endDate.getTime() - startDate.getTime();

                if (diffMs < 600000) {
                  // Less than 10 minutes
                  const minutes = Math.floor(diffMs / (1000 * 60));
                  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
                  return `${minutes} ${t("common.time.minutes")} ${seconds} ${t("common.time.seconds")}`;
                } else if (diffMs > 3600000) {
                  // More than 60 minutes
                  const hours = Math.floor(diffMs / (1000 * 60 * 60));
                  const minutes = Math.floor(
                    (diffMs % (1000 * 60 * 60)) / (1000 * 60),
                  );
                  return `${hours} ${t("common.time.hours")} ${minutes} ${t("common.time.minutes")}`;
                } else {
                  return `${Math.floor(diffMs / (1000 * 60))}${t("common.time.minutes")}`;
                }
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Questions and Answers */}
      <div className="space-y-6">
        {questionsWithStudentAnswers.map(
          (
            question: StudentAssignmentDetailTeacherPerspectiveResponseDtoQuestionsWithStudentAnswersItem,
            index: number,
          ) => {
            const isCorrect = question.question.point === question.score;

            return (
              <div
                key={question.question.id}
                className={`border-2 ${
                  isCorrect
                    ? "border-green-200 dark:border-green-900"
                    : "border-red-200 dark:border-red-900"
                } rounded-lg p-6 bg-white dark:bg-gray-800`}
              >
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-full text-sm">
                    {t("assignments.student.question")} {index + 1}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-sm ${
                      question.score === question.question.point
                        ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100"
                        : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100"
                    }`}
                  >
                    {t("assignments.student.points")}: {question.score ?? "0"}/
                    {question.question.point}
                  </span>
                </div>

                <QuestionViewer index={index + 1} question={question} />
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default SingleStudentAssignmentView;
